import { ApiError } from '@/lib/api/errors';

/** Which frontend a company is served by. Mirrors the backend's `companies.platform`. */
export type Platform = 'legacy' | 'falcon';

/**
 * Sent to the backend on platform-routed requests. Its presence is how the
 * backend's PlatformGuard tells project-frontier from Angular, which sends
 * nothing.
 */
export const CLIENT_PLATFORM_HEADER = 'X-Client-Platform';

/**
 * Set by the shared-host edge (a CloudFront origin custom header, overriding any
 * viewer-sent value) on every request it forwards to falcon-web. Present as `on`
 * means "apply platform routing"; anything else, including absent (falcon.dev),
 * means this host is a pure-Falcon QA host. See spec Amendment 13a.
 */
export const CP_PLATFORM_ROUTING_HEADER = 'x-cp-platform-routing';

/** Sent to the backend when routing is not on: it skips the platform check. */
export const PLATFORM_ROUTING_HEADER = 'X-Platform-Routing';
export const PLATFORM_ROUTING_OFF = 'off';

/** Minimal shape shared by `Headers`, `NextRequest.headers` and `next/headers`. */
export interface HeaderReader {
  get(name: string): string | null;
}

/** True only when the edge marked the incoming request `x-cp-platform-routing: on`. */
export function isPlatformRoutingRequest(incoming: HeaderReader): boolean {
  return incoming.get(CP_PLATFORM_ROUTING_HEADER)?.trim().toLowerCase() === 'on';
}

/**
 * The platform header(s) for a backend call made on behalf of `incoming`.
 * A missing `incoming` (no request context, e.g. a test) means routing OFF.
 */
export function platformHeadersFor(
  incoming: HeaderReader | null | undefined,
): Record<string, string> {
  return incoming && isPlatformRoutingRequest(incoming)
    ? { [CLIENT_PLATFORM_HEADER]: 'falcon' }
    : { [PLATFORM_ROUTING_HEADER]: PLATFORM_ROUTING_OFF };
}

/** The backend's answer when the session's company is on the other platform. */
export const PLATFORM_MOVED_CODE = 'PLATFORM_MOVED';

/** Where a moved user is sent. `proxy.ts` clears every auth cookie on arrival. */
export const MOVED_LOGIN_URL = '/login?notice=moved';

export function isPlatformMoved(e: unknown): e is ApiError {
  return e instanceof ApiError && e.status === 409 && e.code === PLATFORM_MOVED_CODE;
}
